// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color, xScreen } from "@plumberssupply/XDL/Utilities";
import { FC } from "react";
import React from "react";
import { css } from "styled-components";

const enum fields {
    itemsList = "itemsList",
    buttonLabel = "buttonLabel",
    buttonVariant = "buttonVariant",
    buttonLink = "buttonLink",
}

interface OwnProps {
    fields: {
        [fields.buttonLabel]: string;
        [fields.buttonVariant]: ButtonVariants;
        [fields.buttonLink]: LinkFieldValue;
        [fields.itemsList]: {
            fields: {
                imageUrl: string;
                linkTitle: string;
                link: LinkFieldValue;
            };
        }[];
    };
}

type Props = OwnProps & WidgetProps;

interface UserQuickLinksStyles {
    wrapper?: InjectableCss;
    infoSection?: InjectableCss;
    itemsSection?: InjectableCss;
    title?: TypographyPresentationProps;
    button?: ButtonPresentationProps;
    blockContent?: InjectableCss;
    blockContentInfo?: InjectableCss;
    item?: {
        image?: LazyImageProps;
        link?: LinkPresentationProps;
        title?: TypographyPresentationProps;
    };
}

const UserQuickLinksStyles: UserQuickLinksStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            background-color: ${color("common.white")};
            ${xScreen("lte", 1122)} {
                flex-direction: column;
            }
        `,
    },
    infoSection: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            flex-direction: column;
            width: 418px;
            background-color: ${color("common.ui6")};
            padding: 20px;

            ${xScreen(768, 1122)} {
                flex-direction: row;
                justify-content: space-between;
            }

            ${xScreen("lte", 1122)} {
                width: 100%;
            }
        `,
    },
    title: {
        variant: "h3",
        css: css`
            ${xScreen(768, 1122)} {
                margin-bottom: 0;
            }
        `,
    },
    button: {
        mergeCss: true,
        css: css`
            weight: 800;
        `,
    },
    itemsSection: {
        css: css`
            flex-grow: 1;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px;

            ${xScreen("lte", "sm")} {
                align-items: flex-start;
                flex-wrap: wrap;
            }
        `,
    },
    blockContent: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            flex: 1;

            ${xScreen("lte", "sm")} {
                max-width: 128px;
            }

            ${xScreen("lt", 768)} {
                max-width: 50%;
                flex-basis: 50%;
                margin-bottom: 24px;
            }
        `,
    },
    blockContentInfo: {
        css: css`
            display: flex;
            align-items: flex-start;
            flex-direction: column;
        `,
    },
    item: {
        image: {
            width: "60px",
            height: "48px",
            css: css`
                flex-shrink: 0;
                margin-bottom: 12px;
                ${xScreen("lte", "md")} {
                    margin-bottom: 15px;
                }
            `,
        },
        title: {
            css: css`
                display: block;
                line-height: 24px;
            `,
        },
    },
};

const styles = UserQuickLinksStyles;

const UserQuickLinks: FC<Props> = ({ fields }) => {
    const { firstName } = useAppSelector(state => {
        return {
            firstName: state.context.session.firstName,
        };
    });
    const buttonLink = useGetLink(fields.buttonLink);
    const itemsLinks = useGetLinks(fields.itemsList, o => o.fields.link);
    const history = useHistory();

    const onClick = (url?: string) => url && history.push(url);

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="UserQuickLinks">
            <StyledWrapper {...styles.infoSection}>
                <Typography {...styles.title} data-test-selector="UserQuickLinks_title">
                    {translate("Welcome")}, {firstName}!
                </Typography>
                <Button {...styles.button} onClick={() => onClick(buttonLink?.url)} variant={fields.buttonVariant}>
                    {fields.buttonLabel}
                </Button>
            </StyledWrapper>
            <StyledWrapper {...styles.itemsSection}>
                {fields.itemsList?.map((itemField, index) => {
                    const item = {
                        linkTitle: itemField.fields.linkTitle,
                        imageUrl: itemField.fields.imageUrl,
                        link: itemsLinks?.[index].url || "",
                    };

                    return (
                        <StyledWrapper {...styles.blockContent} key={item.linkTitle}>
                            <Link href={item.link} {...styles.item?.link}>
                                <LazyImage src={item.imageUrl} altText={item.linkTitle} {...styles.item?.image} />
                                <Typography {...styles.item?.title}>{item.linkTitle}</Typography>
                            </Link>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: UserQuickLinks,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                fieldType: "General",
                defaultValue: "View Account Dashboard",
            },
            {
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                fieldType: "General",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
            },
            {
                name: "itemsList",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                isVisible: item => !item.fields.isAuthenticated,
                fieldDefinitions: [
                    {
                        name: "imageUrl",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "linkTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Url", value: "" },
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
